import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";
import { Modal } from "@/components/ui/Modal";
import { GameMergeModal } from "@/components/admin/GameMergeModal";
import type { AdminGameDetail } from "@/types";

const ADMIN_GAME_QUERY = gql`
  query AdminGame($id: ID!) {
    adminGame(id: $id) {
      id
      title
      searchableTitle
      yearPublished
      dataSource
      bggId
      bggNotFound
      bggLastSyncedAt
      bggRating
      bggRank
      bayesAverage
      usersRated
      localRating
      localVotesCount
      displayRating
      ratingSource
      complexity
      minPlayers
      maxPlayers
      minPlaytime
      maxPlaytime
      minAge
      languageIndependent
      isExpansion
      imageUrl
      thumbnailUrl
      descriptionEn
      descriptionLt
      designer
      publisher
      designersList
      publishersList
      mechanics
      genres
      categories
      families
      expansions
      artists
      boxLength
      boxWidth
      boxHeight
      approvalStatus
      public
      alternateNames
      activeListingsCount
      shopListings {
        id
        shopName
        listingUrl
        price
        priceUpdatedAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_GAME_APPROVAL_MUTATION = gql`
  mutation UpdateGameApproval($id: ID!, $approvalStatus: String!) {
    updateGameApproval(id: $id, approvalStatus: $approvalStatus) {
      game {
        id
        approvalStatus
      }
      errors
    }
  }
`;

const DELETE_GAME_MUTATION = gql`
  mutation DeleteGame($id: ID!) {
    deleteGame(id: $id) {
      success
      errors
    }
  }
`;


function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("lt-LT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Field({
  label,
  value,
  warn,
}: {
  label: string;
  value: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 py-2">
      <span className="shrink-0 text-sm font-medium text-gray-500">
        {label}
      </span>
      <span
        className={`text-right text-sm ${warn ? "font-medium text-red-600" : "text-gray-900"}`}
      >
        {value != null && value !== "" ? (
          value
        ) : (
          <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-400">
            trūksta
          </span>
        )}
      </span>
    </div>
  );
}

function TagList({ items, color }: { items: string[]; color: string }) {
  if (!items || items.length === 0) return <span className="text-gray-300">—</span>;
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-50 text-blue-700",
    purple: "bg-purple-50 text-purple-700",
    green: "bg-green-50 text-green-700",
    orange: "bg-orange-50 text-orange-700",
    gray: "bg-gray-100 text-gray-700",
  };
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <span
          key={item}
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${colorClasses[color] || colorClasses.gray}`}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-400">
        {title}
      </h2>
      {children}
    </div>
  );
}

export function AdminGamePreviewPage() {
  const { id } = useParams<{ id: string }>();
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["adminGame", id],
    queryFn: () =>
      graphqlClient.request<{ adminGame: AdminGameDetail | null }>(
        ADMIN_GAME_QUERY,
        { id },
      ),
    enabled: !!id,
  });

  const approvalMutation = useMutation({
    mutationFn: (approvalStatus: string) =>
      graphqlClient.request(UPDATE_GAME_APPROVAL_MUTATION, {
        id,
        approvalStatus,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminGame", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      graphqlClient.request<{
        deleteGame: { success: boolean; errors: string[] };
      }>(DELETE_GAME_MUTATION, { id }),
    onSuccess: (data) => {
      if (data.deleteGame.success) {
        navigate("/admin/games");
      }
    },
  });

  const game = data?.adminGame;

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-500">Žaidimas nerastas</p>
        <Link
          to="/admin/games"
          className="mt-4 inline-block text-sm text-primary-800 hover:underline"
        >
          Grįžti
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6 flex items-center gap-2 text-sm text-gray-500">
        <Link to="/admin" className="hover:text-primary-800">
          Admin
        </Link>
        <span>/</span>
        <Link to="/admin/games" className="hover:text-primary-800">
          Žaidimai
        </Link>
        <span>/</span>
        <span className="text-gray-900">{game.title}</span>
      </nav>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {game.title}
            {game.yearPublished && (
              <span className="ml-2 text-lg font-normal text-gray-400">
                ({game.yearPublished})
              </span>
            )}
          </h1>
          <div className="mt-1 flex flex-wrap gap-2">
            {game.approvalStatus === "approved" ? (
              <Badge variant="success">Patvirtintas</Badge>
            ) : game.approvalStatus === "rejected" ? (
              <Badge variant="danger">Atmestas</Badge>
            ) : (
              <Badge variant="default">Laukia patvirtinimo</Badge>
            )}
            {game.public ? (
              <Badge variant="success">Viešas</Badge>
            ) : (
              <Badge variant="danger">Neviešas</Badge>
            )}
            {game.isExpansion && <Badge variant="default">Papildymas</Badge>}
            {game.languageIndependent && (
              <Badge variant="default">Nepriklausomas nuo kalbos</Badge>
            )}
            {game.bggId ? (
              <Badge variant="success">BGG: {game.bggId}</Badge>
            ) : game.bggNotFound ? (
              <Badge variant="danger">BGG nerastas</Badge>
            ) : (
              <Badge variant="default">BGG neieškota</Badge>
            )}
          </div>
          {game.approvalStatus !== "approved" && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => approvalMutation.mutate("approved")}
                disabled={approvalMutation.isPending}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                Patvirtinti
              </button>
              {game.approvalStatus !== "rejected" && (
                <button
                  onClick={() => approvalMutation.mutate("rejected")}
                  disabled={approvalMutation.isPending}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Atmesti
                </button>
              )}
            </div>
          )}
          {game.approvalStatus === "approved" && (
            <div className="mt-3">
              <button
                onClick={() => approvalMutation.mutate("rejected")}
                disabled={approvalMutation.isPending}
                className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Atmesti
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link
            to={`/games/${game.id}`}
            className="text-sm text-primary-800 hover:underline"
          >
            Viešas puslapis →
          </Link>
          <button
            onClick={() => setMergeModalOpen(true)}
            className="rounded-lg border border-primary-300 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-primary-50"
          >
            Sujungti / Susieti
          </button>
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Ištrinti žaidimą
          </button>
        </div>
      </div>

      <GameMergeModal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        sourceGame={game}
      />

      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Ištrinti žaidimą"
      >
        <p className="mb-2 text-sm text-gray-700">
          Ar tikrai norite ištrinti <strong>{game.title}</strong>?
        </p>
        <p className="mb-4 text-sm text-red-600">
          Bus ištrinti visi susiję skelbimai ({game.activeListingsCount}),
          parduotuvių įrašai ({game.shopListings.length}) ir kiti duomenys. Šio
          veiksmo negalima atšaukti.
        </p>
        {deleteMutation.isError && (
          <p className="mb-3 text-sm text-red-600">
            Klaida trinant žaidimą. Bandykite dar kartą.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setDeleteConfirmOpen(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Atšaukti
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending ? "Trinama..." : "Ištrinti"}
          </button>
        </div>
      </Modal>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Image column */}
        <div className="space-y-6 lg:col-span-1">
          <Section title="Viršelis">
            {game.imageUrl ? (
              <img
                src={game.imageUrl}
                alt={game.title}
                className="w-full rounded-lg"
              />
            ) : (
              <GamePlaceholder className="aspect-square" />
            )}
            {game.thumbnailUrl && game.thumbnailUrl !== game.imageUrl && (
              <div className="mt-3">
                <p className="mb-1 text-xs text-gray-400">Miniatiūra</p>
                <img
                  src={game.thumbnailUrl}
                  alt="thumbnail"
                  className="h-20 rounded"
                />
              </div>
            )}
          </Section>

          <Section title="Nuorodos">
            <div className="space-y-2">
              {game.bggId && (
                <a
                  href={`https://boardgamegeek.com/boardgame/${game.bggId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-primary-800 hover:underline"
                >
                  BoardGameGeek →
                </a>
              )}
              {game.imageUrl && (
                <a
                  href={game.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-primary-800 hover:underline"
                >
                  Pilnas paveikslėlis →
                </a>
              )}
            </div>
          </Section>
        </div>

        {/* Main content */}
        <div className="space-y-6 lg:col-span-3">
          {/* Identity & Meta */}
          <div className="grid gap-6 md:grid-cols-2">
            <Section title="Identifikacija">
              <Field label="ID" value={game.id} />
              <Field label="Pavadinimas" value={game.title} />
              <Field label="Paieškos pav." value={game.searchableTitle} />
              <Field label="Metai" value={game.yearPublished} />
              <Field label="Šaltinis" value={game.dataSource} />
              <Field label="Sukurtas" value={formatDate(game.createdAt)} />
              <Field label="Atnaujintas" value={formatDate(game.updatedAt)} />
            </Section>

            <Section title="BGG duomenys">
              <Field label="BGG ID" value={game.bggId} />
              <Field
                label="BGG nerastas"
                value={game.bggNotFound ? "Taip" : "Ne"}
                warn={game.bggNotFound}
              />
              <Field
                label="Pask. sinchronizacija"
                value={formatDate(game.bggLastSyncedAt)}
              />
              <Field label="BGG reitingas" value={game.bggRating?.toFixed(2)} />
              <Field label="BGG rankas" value={game.bggRank} />
              <Field
                label="Bayes vidurkis"
                value={game.bayesAverage?.toFixed(2)}
              />
              <Field label="Balsų sk." value={game.usersRated} />
            </Section>
          </div>

          {/* Ratings */}
          <Section title="Reitingai">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-400">Rodomas</p>
                <p className="text-xl font-bold">
                  {game.displayRating?.toFixed(1) ?? "—"}
                </p>
                <p className="text-xs text-gray-400">
                  {game.ratingSource ?? "—"}
                </p>
              </div>
              <div className="rounded-lg bg-orange-50 p-3 text-center">
                <p className="text-xs text-gray-400">BGG</p>
                <p className="text-xl font-bold text-orange-700">
                  {game.bggRating?.toFixed(1) ?? "—"}
                </p>
              </div>
              <div className="rounded-lg bg-blue-50 p-3 text-center">
                <p className="text-xs text-gray-400">Vietinis</p>
                <p className="text-xl font-bold text-blue-700">
                  {game.localRating?.toFixed(1) ?? "—"}
                </p>
                <p className="text-xs text-gray-400">
                  {game.localVotesCount ?? 0} balsų
                </p>
              </div>
            </div>
          </Section>

          {/* Game details */}
          <div className="grid gap-6 md:grid-cols-2">
            <Section title="Žaidimo informacija">
              <Field
                label="Žaidėjai"
                value={
                  game.minPlayers != null
                    ? game.maxPlayers && game.maxPlayers !== game.minPlayers
                      ? `${game.minPlayers}–${game.maxPlayers}`
                      : game.minPlayers
                    : null
                }
              />
              <Field
                label="Trukmė"
                value={
                  game.minPlaytime != null
                    ? game.maxPlaytime && game.maxPlaytime !== game.minPlaytime
                      ? `${game.minPlaytime}–${game.maxPlaytime} min`
                      : `${game.minPlaytime} min`
                    : null
                }
              />
              <Field
                label="Sudėtingumas"
                value={game.complexity?.toFixed(2)}
              />
              <Field label="Min. amžius" value={game.minAge} />
              <Field
                label="Nuo kalbos nepriklausomas"
                value={game.languageIndependent ? "Taip" : "Ne"}
              />
              <Field
                label="Papildymas"
                value={game.isExpansion ? "Taip" : "Ne"}
              />
            </Section>

            <Section title="Dėžės matmenys">
              <Field
                label="Ilgis"
                value={game.boxLength ? `${game.boxLength} cm` : null}
              />
              <Field
                label="Plotis"
                value={game.boxWidth ? `${game.boxWidth} cm` : null}
              />
              <Field
                label="Aukštis"
                value={game.boxHeight ? `${game.boxHeight} cm` : null}
              />
              <Field
                label="Tūris"
                value={
                  game.boxLength && game.boxWidth && game.boxHeight
                    ? `${(game.boxLength * game.boxWidth * game.boxHeight).toFixed(0)} cm³`
                    : null
                }
              />
            </Section>
          </div>

          {/* Alternate names */}
          <Section title="Alternatyvūs pavadinimai">
            {game.alternateNames.length > 0 ? (
              <TagList items={game.alternateNames} color="gray" />
            ) : (
              <p className="text-sm text-gray-400">Nėra</p>
            )}
          </Section>

          {/* Tags */}
          <div className="grid gap-6 md:grid-cols-2">
            <Section title="Mechanikos">
              <TagList items={game.mechanics ?? []} color="blue" />
            </Section>
            <Section title="Žanrai">
              <TagList items={game.genres ?? []} color="purple" />
            </Section>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Section title="Kategorijos">
              <TagList items={game.categories ?? []} color="green" />
            </Section>
            <Section title="Šeimos">
              <TagList items={game.families ?? []} color="orange" />
            </Section>
          </div>

          {/* People */}
          <div className="grid gap-6 md:grid-cols-3">
            <Section title="Dizaineriai">
              <Field label="Pagrindinis" value={game.designer} />
              <TagList items={game.designersList ?? []} color="gray" />
            </Section>
            <Section title="Leidėjai">
              <Field label="Pagrindinis" value={game.publisher} />
              <TagList items={game.publishersList ?? []} color="gray" />
            </Section>
            <Section title="Menininkai">
              <TagList items={game.artists ?? []} color="gray" />
            </Section>
          </div>

          {/* Expansions */}
          {game.expansions && game.expansions.length > 0 && (
            <Section title="Papildymai">
              <TagList items={game.expansions} color="gray" />
            </Section>
          )}

          {/* Descriptions */}
          <Section title="Aprašymas (EN)">
            {game.descriptionEn ? (
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: game.descriptionEn }}
              />
            ) : (
              <p className="text-sm font-medium text-red-500">Trūksta</p>
            )}
          </Section>

          <Section title="Aprašymas (LT)">
            {game.descriptionLt ? (
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: game.descriptionLt }}
              />
            ) : (
              <p className="text-sm font-medium text-red-500">Trūksta</p>
            )}
          </Section>

          {/* Shop listings */}
          <Section title={`Parduotuvės (${game.shopListings.length})`}>
            {game.shopListings.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {game.shopListings.map((sl) => (
                  <div
                    key={sl.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div>
                      <a
                        href={sl.listingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary-800 hover:underline"
                      >
                        {sl.shopName}
                      </a>
                    </div>
                    <div className="text-right">
                      {sl.price != null ? (
                        <span className="font-semibold">
                          {sl.price.toFixed(2)} EUR
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      {sl.priceUpdatedAt && (
                        <p className="text-xs text-gray-400">
                          {formatDate(sl.priceUpdatedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Nėra</p>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
